import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as chatApi from '../../api/chat';
import type { Conversation, Message, Reaction, Pin } from '../../api/chat';
import TaskCreate from '../Task/TaskCreate';
import {
  Send, Plus, Search, Edit2, Trash2, Smile, Paperclip, X,
  Users, MessageSquare, Loader2,
  ListTodo, MoreVertical, Hash, Info,
  ChevronDown,  Shield, UserMinus,
  Mail, Phone, Briefcase, Building2, ArrowLeft,
  Bold, Italic, Code,
} from 'lucide-react';
import departmentApi from '../../api/departmentApi';
import type { EligibleUser } from '../../api/chat';
import {
  EMOJIS, PRIORITIES, BASE_URL, AVATAR_COLORS,
  fmtTime, fmtDate, fmtFileSize, convDisplayName,
  getInitials, getAvatarColor, isEditableNow,
  groupMessages,
  type PendingMessage,
} from './utils/format';
import { useChatWs } from './hooks/useChatWs';
import Avatar from './components/Avatar';
import { ConvSkeleton, MsgSkeleton } from './components/Skeletons';
import ConvRow from './components/ConversationRow';
import AttachmentBubble from './components/AttachmentBubble';
import MessageBubble from './components/MessageBubble';
import DateDivider from './components/DateDivider';
import PinnedBar from './components/PinnedBar';
import CreateTaskModal from './components/CreateTaskModal';
import NewConvModal from './components/NewConvModal';
import TypingIndicator from './components/TypingIndicator';
import ScrollToBottomBtn from './components/ScrollToBottomBtn';
import EmptyState from './components/EmptyState';
import ThreadPanel from './components/ThreadPanel';
import MentionsAutocomplete, { type MentionsAutocompleteHandle } from './components/MentionsAutocomplete';

type PresenceFilter = 'all' | 'online' | 'away' | 'offline';

// ─── @mention helpers ─────────────────────────────────────────────────────────
function getMentionQuery(text: string, caret: number): string | null {
  let i = caret - 1;
  let query = '';
  while (i >= 0) {
    const c = text[i];
    if (/\s/.test(c)) return null;
    if (c === '@') {
      if (i === 0 || /\s/.test(text[i - 1])) return query;
      return null;
    }
    query = c + query;
    i--;
  }
  return null;
}

// ─── Main Chat Component ──────────────────────────────────────────────────────

export default function Chat() {
  const { user } = useAuth();
  const token = localStorage.getItem('accessToken');

  const myId = user?.id || '';

  // ── Core state ────────────────────────────────────────────────────────────
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PendingMessage[]>([]);
  const [input, setInput] = useState('');
  const [editingMsg, setEditingMsg] = useState<Message | null>(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({}); // userId → name
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [awayUserIds, setAwayUserIds] = useState<Set<string>>(new Set());
  const [showInputEmoji, setShowInputEmoji] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const [convSearch, setConvSearch] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [convError, setConvError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [taskModal, setTaskModal] = useState<Message | null>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [activeOnlineIds, setActiveOnlineIds] = useState<Set<string>>(new Set());
  const [globalOnlineIds, setGlobalOnlineIds] = useState<Set<string>>(new Set());
  const [blankTaskConvId, setBlankTaskConvId] = useState<string | null>(null);
  const [seenMsgIds, setSeenMsgIds] = useState<Set<string>>(new Set());

  // Issue 4: profile drawer
  const [profileMember, setProfileMember] = useState<any | null>(null);
  // Issue 6: filters
  const [deptFilter, setDeptFilter] = useState<string>('');
  const [designationFilter, setDesignationFilter] = useState<string>('');
  const [presenceFilter, setPresenceFilter] = useState<PresenceFilter>('all');
  const [departments, setDepartments] = useState<Array<{ id: string | number; name: string }>>([]);
  // Issue 7: header menu
  const [showChatMenu, setShowChatMenu] = useState(false);
  // Issue 3: highlighted message
  const [highlightMsgId, setHighlightMsgId] = useState<string | null>(null);
  // Pinned messages for active conversation
  const [pins, setPins] = useState<Pin[]>([]);
  // Thread panel state
  const [threadParent, setThreadParent] = useState<Message | null>(null);
  const [threadReplies, setThreadReplies] = useState<PendingMessage[]>([]);
  const threadParentIdRef = useRef<string | null>(null);
  useEffect(() => { threadParentIdRef.current = threadParent?.id || null; }, [threadParent]);

  // Mentions (@autocomplete)
  const [chatUsers, setChatUsers] = useState<EligibleUser[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const mentionsRef = useRef<MentionsAutocompleteHandle>(null);
  const pendingMentionsRef = useRef<Map<string, string>>(new Map()); // userId -> displayName

  // ── Refs ──────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const activeConvIdRef = useRef<string | null>(null);
  const wsSendRef = useRef<(data: object) => void>(() => {});

  // Keep refs in sync
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);

  // Task 5: open conversation from URL ?open=<conv_id>&msg=<message_id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    const msgId = params.get('msg');
    if (openId) {
      setActiveConvId(openId);
      if (msgId) setHighlightMsgId(msgId);
      window.history.replaceState({}, '', '/chat');
    }
  }, []);

  // Scroll to/highlight a specific message once loaded
  useEffect(() => {
    if (!highlightMsgId || !messages.length) return;
    const el = document.getElementById(`msg-${highlightMsgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const t = setTimeout(() => setHighlightMsgId(null), 1500);
      return () => clearTimeout(t);
    }
  }, [highlightMsgId, messages]);

  // Load eligible users once for @mention autocomplete
  useEffect(() => {
    chatApi.getEligibleUsers()
      .then(list => setChatUsers(Array.isArray(list) ? list : []))
      .catch(() => setChatUsers([]));
  }, []);

  // Issue 6: load departments for filter
  useEffect(() => {
    departmentApi.getDepartments()
      .then((res: any) => {
        const list = Array.isArray(res) ? res : (res?.departments || res?.data || []);
        setDepartments(list.map((d: any) => ({ id: d.id ?? d.department_id, name: d.name || d.department_name })).filter((d: any) => d.name));
      })
      .catch(() => {});
  }, []);

  // Issue 6: refetch conversations when filters change
  useEffect(() => {
    chatApi.listConversations({
      department_id: deptFilter || undefined,
      designation: designationFilter || undefined,
    }).then(list => setConversations(
      [...list].sort((a, b) =>
        new Date(b.last_activity || '').getTime() - new Date(a.last_activity || '').getTime()
      )
    )).catch(() => {});
  }, [deptFilter, designationFilter]);

  // Task 6: poll online status for active conversation (every 30s)
  useEffect(() => {
    if (!activeConvId) return;
    const fetch = () => {
      chatApi.getOnlineMembers(activeConvId)
        .then(res => setActiveOnlineIds(new Set(res.online)))
        .catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 30_000);
    return () => clearInterval(id);
  }, [activeConvId]);

  // Task 6: poll online status globally for sidebar (every 60s) using first conversation
  useEffect(() => {
    if (!conversations.length) return;
    const fetchAll = async () => {
      const ids = new Set<string>();
      await Promise.all(
        conversations.slice(0, 10).map(c =>
          chatApi.getOnlineMembers(c.id)
            .then(res => res.online.forEach(id => ids.add(id)))
            .catch(() => {})
        )
      );
      setGlobalOnlineIds(ids);
    };
    fetchAll();
    const timer = setInterval(fetchAll, 60_000);
    return () => clearInterval(timer);
  }, [conversations.length]); // eslint-disable-line

  const activeConv = conversations.find(c => c.id === activeConvId) || null;

  const getConvName = (c: Conversation) => convDisplayName(c, myId);

  const isOtherOnline = (c: Conversation) => {
    if (c.type !== 'dm') return false;
    const other = c.members?.find(m => m.user_id !== myId);
    if (!other) return false;
    if (c.id === activeConvId) return activeOnlineIds.has(other.user_id);
    return globalOnlineIds.has(other.user_id);
  };

  const getGroupOnlineCount = (c: Conversation) => {
    const pool = c.id === activeConvId ? activeOnlineIds : globalOnlineIds;
    return (c.members || []).filter(m => m.user_id !== myId && pool.has(m.user_id)).length;
  };

  const handleDeleteDm = async () => {
    if (!activeConvId) return;
    if (!window.confirm('Delete this chat? You will not see future messages.')) return;
    try {
      await chatApi.deleteDm(activeConvId);
      setConversations(prev => prev.filter(c => c.id !== activeConvId));
      setActiveConvId(null);
      setShowChatMenu(false);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to delete chat');
    }
  };

  const handleDeleteGroup = async () => {
    if (!activeConvId) return;
    if (!window.confirm('Delete this group? This cannot be undone.')) return;
    try {
      await chatApi.deleteConversation(activeConvId);
      setConversations(prev => prev.filter(c => c.id !== activeConvId));
      setActiveConvId(null);
      setShowMembersPanel(false);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!activeConvId) return;
    try {
      await chatApi.removeMember(activeConvId, userId);
      setConversations(prev => prev.map(c =>
        c.id === activeConvId
          ? { ...c, members: (c.members || []).filter(m => m.user_id !== userId) }
          : c
      ));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to remove member');
    }
  };

  // ── Broadcast unread count to header ──────────────────────────────────────
  useEffect(() => {
    const total = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
    window.dispatchEvent(new CustomEvent('chat:unread', { detail: { count: total } }));
  }, [conversations]);

  // ── WebSocket event handler ───────────────────────────────────────────────
  const handleWsEvent = useCallback((evt: any) => {
    const currentConvId = activeConvIdRef.current;

    switch (evt.type) {
      case 'new_message': {
        const m: Message = evt.message;
        const isActive = currentConvId === m.conversation_id;
        const isMine = m.sender_id === myId;
        const isThreadReply = !!m.thread_parent_id;

        if (isActive) {
          if (isThreadReply) {
            // Thread reply: bump parent's reply_count on main list; do NOT append to main timeline.
            setMessages(prev => prev.map(x => x.id === m.thread_parent_id
              ? { ...x, reply_count: ((x as any).reply_count || 0) + 1 } as PendingMessage
              : x
            ));
            // If this reply belongs to the open thread, append it (deduped, replacing optimistic).
            if (threadParentIdRef.current && threadParentIdRef.current === m.thread_parent_id) {
              setThreadReplies(prev => {
                const withoutPending = isMine ? prev.filter(x => !x._pending) : prev;
                if (withoutPending.find(x => x.id === m.id)) return withoutPending;
                return [...withoutPending, m];
              });
            }
          } else {
            setMessages(prev => {
              // Replace optimistic pending with confirmed
              const withoutPending = isMine ? prev.filter(x => !x._pending) : prev;
              if (withoutPending.find(x => x.id === m.id)) return withoutPending;
              return [...withoutPending, m];
            });
          }

          // Auto-mark as seen if in active conversation
          if (!isMine) {
            chatApi.markSeen(m.conversation_id, [m.id]).catch(() => {});
            wsSendRef.current({ type: 'seen', conversation_id: m.conversation_id, message_ids: [m.id] });
          }
        }

        // Update sidebar (skip for thread replies — they don't change the conversation's top-level activity)
        if (!isThreadReply) {
          setConversations(prev =>
            prev.map(c => c.id === m.conversation_id
              ? {
                ...c,
                last_message: m,
                last_activity: m.created_at,
                unread_count: (isActive || isMine) ? 0 : (c.unread_count || 0) + 1,
              }
              : c
            ).sort((a, b) =>
              new Date(b.last_activity || '').getTime() - new Date(a.last_activity || '').getTime()
            )
          );
        }
        break;
      }

      case 'message_edited':
        setMessages(prev => prev.map(m => m.id === evt.message.id ? { ...evt.message } : m));
        break;

      case 'message_deleted':
        setMessages(prev => prev.map(m => m.id === evt.message_id ? { ...m, is_deleted: true } : m));
        break;

      case 'typing': {
        if (evt.conversation_id !== currentConvId) break;
        setTypingUsers(prev => {
          const updated = { ...prev };
          if (evt.active) {
            updated[evt.user_id] = evt.user_name || evt.user_id;
          } else {
            delete updated[evt.user_id];
          }
          return updated;
        });
        break;
      }

      case 'seen_receipt': {
        const ids: string[] = evt.message_ids || [];
        if (ids.length) {
          setSeenMsgIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => next.add(id));
            return next;
          });
          // Zero out unread badge for active conversation when other user marks seen
          if (evt.conversation_id === currentConvId) {
            setConversations(prev => prev.map(c =>
              c.id === evt.conversation_id ? { ...c, unread_count: 0 } : c
            ));
          }
        }
        break;
      }

      case 'reaction_update':
        setMessages(prev => prev.map(m => m.id === evt.message_id ? { ...m, reactions: evt.reactions } : m));
        break;

      case 'presence':
        if (evt.status === 'online') {
          setOnlineUsers(prev => { const n = new Set(prev); n.add(evt.user_id); return n; });
          setAwayUserIds(prev => { const n = new Set(prev); n.delete(evt.user_id); return n; });
        } else if (evt.status === 'away') {
          setOnlineUsers(prev => { const n = new Set(prev); n.delete(evt.user_id); return n; });
          setAwayUserIds(prev => { const n = new Set(prev); n.add(evt.user_id); return n; });
        } else {
          setOnlineUsers(prev => { const n = new Set(prev); n.delete(evt.user_id); return n; });
          setAwayUserIds(prev => { const n = new Set(prev); n.delete(evt.user_id); return n; });
        }
        break;

      case 'task_created':
        if (evt.message && evt.message.conversation_id === currentConvId) {
          setMessages(prev => prev.find(m => m.id === evt.message.id) ? prev : [...prev, evt.message]);
        }
        break;

      case 'message_pinned': {
        const pin: Pin | undefined = evt.pin;
        if (pin && pin.conversation_id === currentConvId) {
          setPins(prev => (prev.some(p => p.message_id === pin.message_id) ? prev : [pin, ...prev]));
        }
        break;
      }

      case 'message_unpinned': {
        if (evt.conversation_id === currentConvId) {
          setPins(prev => prev.filter(p => p.message_id !== evt.message_id));
        }
        break;
      }

      case 'conversation_created':
        if (evt.conversation) {
          setConversations(prev =>
            prev.find(c => c.id === evt.conversation.id) ? prev : [evt.conversation, ...prev]
          );
        }
        break;
    }
  }, [myId]);

  const wsSend = useChatWs(token, handleWsEvent);
  useEffect(() => { wsSendRef.current = wsSend; }, [wsSend]);

  // ── Presence / idle timer ─────────────────────────────────────────────────
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const myPresenceRef = useRef<'online' | 'away'>('online');
  useEffect(() => {
    const broadcast = (status: 'online' | 'away' | 'offline') => {
      if (status !== 'offline' && myPresenceRef.current === status) return;
      myPresenceRef.current = status === 'offline' ? 'away' : status;
      wsSendRef.current({ type: 'presence', status });
    };
    const resetIdle = () => {
      broadcast('online');
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => broadcast('away'), 10 * 60 * 1000);
    };
    resetIdle();
    const events = ['mousemove', 'keydown', 'click', 'touchstart'] as const;
    events.forEach(ev => window.addEventListener(ev, resetIdle, { passive: true }));
    const onUnload = () => broadcast('offline');
    window.addEventListener('beforeunload', onUnload);
    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach(ev => window.removeEventListener(ev, resetIdle));
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []); // eslint-disable-line

  // ── Load conversations ────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingConvs(true);
    setConvError('');
    chatApi.listConversations()
      .then(list => setConversations(
        [...list].sort((a, b) =>
          new Date(b.last_activity || '').getTime() - new Date(a.last_activity || '').getTime()
        )
      ))
      .catch((e) => {
        const msg = e?.response?.status === 401
          ? 'Session expired — please log in again.'
          : `Could not load chats: ${e?.response?.data?.detail || e?.message || 'Server error'}`;
        setConvError(msg);
      })
      .finally(() => setLoadingConvs(false));
  }, []);

  // ── Load messages when conversation changes ───────────────────────────────
  useEffect(() => {
    if (!activeConvId) { setPins([]); setThreadParent(null); setThreadReplies([]); return; }
    setMessages([]);
    setHasMore(false);
    setTypingUsers({});
    setSeenMsgIds(new Set());
    setLoadingMsgs(true);
    // Close any open thread when switching conversations
    setThreadParent(null);
    setThreadReplies([]);

    // Load pinned messages in parallel
    chatApi.listPinned(activeConvId).then(setPins).catch(() => setPins([]));

    chatApi.getMessages(activeConvId)
      .then(msgs => {
        setMessages(msgs);
        setHasMore(msgs.length === 50);

        // Auto-scroll to bottom
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        });

        // Mark all loaded messages as seen
        if (msgs.length) {
          const ids = msgs.filter(m => m.sender_id !== myId).map(m => m.id);
          if (ids.length) {
            chatApi.markSeen(activeConvId, ids).catch(() => {});
            wsSendRef.current({ type: 'seen', conversation_id: activeConvId, message_ids: ids });
          }
          setConversations(prev => prev.map(c => c.id === activeConvId ? { ...c, unread_count: 0 } : c));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMsgs(false));
  }, [activeConvId, myId]); // eslint-disable-line

  useEffect(() => {
    if (!activeConvId) return;
    chatApi.getConversation(activeConvId)
      .then(detail => {
        setConversations(prev => prev.map(conv => (
          conv.id === activeConvId
            ? { ...conv, ...detail, last_message: conv.last_message, unread_count: conv.unread_count }
            : conv
        )));
      })
      .catch(() => {});
  }, [activeConvId]);

  // ── Auto-scroll on new message ────────────────────────────────────────────
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // ── Scroll button visibility ──────────────────────────────────────────────
  const handleScroll = () => {
    const c = messagesContainerRef.current;
    if (!c) return;
    const isNearBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 300;
    setShowScrollBtn(!isNearBottom);
  };

  // ── Load older messages ───────────────────────────────────────────────────
  const loadMore = async () => {
    if (!activeConvId || !hasMore || loadingMsgs) return;
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    setLoadingMsgs(true);
    try {
      const firstMsg = messages.find(m => !m._pending);
      const older = await chatApi.getMessages(activeConvId, firstMsg?.id);
      setMessages(prev => [...older, ...prev]);
      setHasMore(older.length === 50);
      // Maintain scroll position
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });
    } finally { setLoadingMsgs(false); }
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = () => {
    if (!activeConvId || !input.trim()) return;
    const text = input.trim();
    setInput('');
    clearTimeout(typingTimer.current);

    // Auto-resize textarea back
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    if (editingMsg) {
      chatApi.editMessage(editingMsg.id, text)
        .then(updated => setMessages(prev => prev.map(m => m.id === updated.id ? updated : m)))
        .catch(() => {});
      setEditingMsg(null);
      return;
    }

    // Optimistic message
    const tempId = `temp-${Date.now()}`;
    const optimistic: PendingMessage = {
      id: tempId,
      conversation_id: activeConvId,
      sender_id: myId,
      sender_name: user?.name || 'You',
      content: text,
      message_type: 'text',
      thread_parent_id: null,
      reply_to_id: null,
      is_edited: false,
      is_deleted: false,
      created_at: new Date().toISOString(),
      reactions: [],
      attachments: [],
      _pending: true,
    };

    const mentionedIds = resolveMentionedIds(text);

    setMessages(prev => [...prev, optimistic]);
    wsSend({
      type: 'message',
      conversation_id: activeConvId,
      content: text,
      message_type: 'text',
      mentioned_users: mentionedIds,
    });
    wsSend({ type: 'typing_stop', conversation_id: activeConvId });
    pendingMentionsRef.current.clear();
    setMentionQuery(null);
  };

  // ── Formatting toolbar (Bold/Italic/Code) ─────────────────────────────────
  const savedSelRef = useRef<[number, number]>([0, 0]);
  const saveSelection = () => {
    const el = textareaRef.current;
    if (el) savedSelRef.current = [el.selectionStart, el.selectionEnd];
  };
  const wrapSelection = (left: string, right: string = left) => {
    const [start, end] = savedSelRef.current;
    const sel = input.slice(start, end) || (left === '**' ? 'bold' : left === '*' ? 'italic' : 'code');
    const next = input.slice(0, start) + left + sel + right + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(start + left.length, start + left.length + sel.length); } catch {}
    });
  };
  const insertAtCursor = (text: string) => {
    const el = textareaRef.current;
    const pos = el ? el.selectionStart : input.length;
    const next = input.slice(0, pos) + text + input.slice(pos);
    setInput(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(pos + text.length, pos + text.length); } catch {}
    });
  };

  // ── Typing indicator ──────────────────────────────────────────────────────
  const handleInput = (val: string) => {
    setInput(val);
    if (!activeConvId) return;
    wsSend({ type: 'typing_start', conversation_id: activeConvId });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      wsSend({ type: 'typing_stop', conversation_id: activeConvId });
    }, 2500);
  };

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    handleInput(val);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    const caret = e.target.selectionStart ?? val.length;
    setMentionQuery(getMentionQuery(val, caret));
  };

  // Insert the picked user into the input, replacing the active @query
  const handleMentionSelect = (u: { id: string; name?: string }) => {
    const el = textareaRef.current;
    const current = input;
    const caret = el?.selectionStart ?? current.length;
    // Walk back from caret to '@'
    let i = caret - 1;
    while (i >= 0 && current[i] !== '@' && !/\s/.test(current[i])) i--;
    if (i < 0 || current[i] !== '@') {
      setMentionQuery(null);
      return;
    }
    const before = current.slice(0, i);
    const after = current.slice(caret);
    const displayName = (u.name || '').trim() || 'user';
    const insertion = `@${displayName} `;
    const next = before + insertion + after;
    setInput(next);
    setMentionQuery(null);
    pendingMentionsRef.current.set(u.id, displayName);

    requestAnimationFrame(() => {
      if (el) {
        const newCaret = (before + insertion).length;
        el.focus();
        try { el.setSelectionRange(newCaret, newCaret); } catch {}
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
      }
    });
  };

  // Resolve mentioned user IDs by scanning the text at send time
  const resolveMentionedIds = (text: string): string[] => {
    const ids = new Set<string>();
    // Prefer previously-picked mentions that still appear in the text
    pendingMentionsRef.current.forEach((name, userId) => {
      if (name && text.includes(`@${name}`)) ids.add(userId);
    });
    // Also catch typed @Name matches against known users (name with one optional space)
    const re = /@([A-Za-z][A-Za-z0-9._-]*(?:\s[A-Za-z][A-Za-z0-9._-]*)?)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const full = m[1].trim();
      // Try full match first, then first word only
      const candidates = [full, full.split(' ')[0]];
      for (const cand of candidates) {
        const hit = chatUsers.find(u => (u.name || '').toLowerCase() === cand.toLowerCase());
        if (hit) { ids.add(hit.id); break; }
      }
    }
    return Array.from(ids);
  };

  // ── Reactions ─────────────────────────────────────────────────────────────
  const handleReact = (msgId: string, emoji: string) => {
    chatApi.toggleReaction(msgId, emoji)
      .then(res => setMessages(prev => prev.map(m => m.id === msgId ? { ...m, reactions: res.reactions as any } : m)))
      .catch(() => {});
  };

  // ── Pin / Unpin ───────────────────────────────────────────────────────────
  const handleTogglePin = async (msg: Message) => {
    const already = pins.some(p => p.message_id === msg.id);
    try {
      if (already) {
        await chatApi.unpinMessage(msg.id);
        setPins(prev => prev.filter(p => p.message_id !== msg.id));
      } else {
        const pin = await chatApi.pinMessage(msg.id);
        setPins(prev => (prev.some(p => p.message_id === pin.message_id) ? prev : [pin, ...prev]));
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update pin');
    }
  };

  const handleUnpin = async (mid: string) => {
    try {
      await chatApi.unpinMessage(mid);
      setPins(prev => prev.filter(p => p.message_id !== mid));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to unpin');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = (msgId: string) => {
    if (!window.confirm('Delete this message?')) return;
    chatApi.deleteMessage(msgId)
      .then(() => setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_deleted: true } : m)))
      .catch(() => {});
  };

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeConvId) return;
    e.target.value = '';

    setFileUploading(true);
    try {
      await chatApi.uploadFile(activeConvId, file);
    } catch {
      alert('File upload failed. Please try again.');
    } finally {
      setFileUploading(false);
    }
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = (q: string) => {
    setSearchQ(q);
    clearTimeout(searchTimer.current);
    if (q.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const raw = await chatApi.searchMessages(q, activeConvId || undefined);
        const msgs = Array.isArray(raw) ? raw : (raw as any).messages || [];
        setSearchResults(msgs);
      } catch {}
    }, 300);
  };

  // ── Filtered conversation list ────────────────────────────────────────────
  const filteredConvs = conversations.filter(c =>
    getConvName(c).toLowerCase().includes(convSearch.toLowerCase())
  );
  const presenceOf = (c: Conversation): 'online' | 'away' | 'offline' => {
    if (c.type !== 'dm') return 'offline';
    const other = c.members?.find(m => m.user_id !== myId);
    if (!other) return 'offline';
    if (isOtherOnline(c)) return 'online';
    if (awayUserIds.has(other.user_id)) return 'away';
    if (other.last_seen_at) {
      const diff = Date.now() - new Date(other.last_seen_at).getTime();
      if (diff < 15 * 60 * 1000) return 'away';
    }
    return 'offline';
  };
  const presenceDotColor = (p: 'online' | 'away' | 'offline') =>
    p === 'online' ? 'bg-green-500' : p === 'away' ? 'bg-yellow-400' : 'bg-gray-400';

  const dmsAll = filteredConvs.filter(c => c.type === 'dm');
  const dms = presenceFilter === 'all'
    ? dmsAll
    : dmsAll.filter(c => presenceOf(c) === presenceFilter);
  const groups = filteredConvs.filter(c => c.type !== 'dm');
  const typingNames = Object.values(typingUsers).filter(Boolean);

  // Designations derived from members
  const designationOptions = React.useMemo(() => {
    const set = new Set<string>();
    conversations.forEach(c => (c.members || []).forEach(m => {
      if (m.designation) set.add(m.designation);
    }));
    return Array.from(set).sort();
  }, [conversations]);

  const getConvSubtitle = (c: Conversation): string | undefined => {
    if (c.type !== 'dm') return `${c.members?.length || 0} members`;
    const other = c.members?.find(m => m.user_id !== myId);
    return other?.designation || undefined;
  };

  // ── Section label component ───────────────────────────────────────────────
  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="px-4 pt-4 pb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
      {children}
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden rounded-xl shadow-sm border border-gray-200">

      {/* ── Left Sidebar ──────────────────────────────────────────────────── */}
      <div className={`${activeConvId ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] flex-1 md:flex-none md:flex-shrink-0 bg-white border-r border-gray-200 flex-col`}>

        {/* Sidebar header */}
        <div className="px-4 py-3.5 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
            <button onClick={() => setShowNewConv(true)}
              className="w-9 h-9 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-105 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="New conversation">
              <Plus size={18} />
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-100 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
              placeholder="Search conversations…"
              value={convSearch}
              onChange={e => setConvSearch(e.target.value)} />
          </div>
          {/* Filters */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <select
              value={deptFilter}
              onChange={e => setDeptFilter(e.target.value)}
              className="text-xs text-gray-600 bg-gray-100 rounded-lg px-2 py-1.5 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
              <option value="">All Departments</option>
              {departments.map(d => (
                <option key={String(d.id)} value={String(d.id)}>{d.name}</option>
              ))}
            </select>
            <select
              value={designationFilter}
              onChange={e => setDesignationFilter(e.target.value)}
              className="text-xs text-gray-600 bg-gray-100 rounded-lg px-2 py-1.5 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
              <option value="">All Designations</option>
              {designationOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto">
          {loadingConvs ? (
            Array.from({ length: 7 }).map((_, i) => <ConvSkeleton key={i} />)
          ) : convError ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-3">
                <X size={22} className="text-red-600" />
              </div>
              <p className="text-sm font-semibold text-red-600 mb-1">Connection Error</p>
              <p className="text-xs text-gray-500 mb-4">{convError}</p>
              <button onClick={() => { setConvError(''); setLoadingConvs(true); chatApi.listConversations().then(list => setConversations(list)).catch(e => setConvError(e?.message || 'Error')).finally(() => setLoadingConvs(false)); }}
                className="text-xs text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-0.5">Retry</button>
            </div>
          ) : filteredConvs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <MessageSquare size={36} className="text-gray-400 mb-3" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-gray-900">No conversations yet</p>
              <p className="text-xs text-gray-500 mt-1">Click + to start a new one</p>
            </div>
          ) : (
            <>
              {dmsAll.length > 0 && (
                <>
                  <SectionLabel>
                    <MessageSquare size={10} />
                    Direct Messages
                  </SectionLabel>
                  <div className="px-3 pb-2 flex items-center gap-1 flex-wrap">
                    {(['all', 'online', 'away', 'offline'] as PresenceFilter[]).map(f => (
                      <button
                        key={f}
                        onClick={() => setPresenceFilter(f)}
                        className={`flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border transition-colors ${
                          presenceFilter === f
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}>
                        {f !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${presenceDotColor(f)}`} />}
                        {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                  {dms.length === 0 ? (
                    <p className="px-4 py-3 text-[11px] text-gray-400 italic">No {presenceFilter} contacts</p>
                  ) : dms.map(c => {
                    const pres = presenceOf(c);
                    return (
                      <div key={c.id} className="relative">
                        <ConvRow c={c}
                          name={getConvName(c)}
                          isActive={c.id === activeConvId}
                          online={pres === 'online'}
                          subtitle={getConvSubtitle(c)}
                          onClick={() => setActiveConvId(c.id)} />
                        <span
                          className={`absolute left-10 bottom-2 w-2.5 h-2.5 rounded-full border-2 border-white pointer-events-none ${presenceDotColor(pres)}`}
                          aria-label={pres}
                        />
                      </div>
                    );
                  })}
                </>
              )}

              {groups.length > 0 && (
                <>
                  <SectionLabel>
                    <Hash size={10} />
                    Groups & Channels
                  </SectionLabel>
                  {groups.map(c => (
                    <ConvRow key={c.id} c={c}
                      name={getConvName(c)}
                      isActive={c.id === activeConvId}
                      subtitle={getConvSubtitle(c)}
                      onClick={() => setActiveConvId(c.id)} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Main Area ─────────────────────────────────────────────────────── */}
      {activeConvId && activeConv ? (
        <div className="flex-1 flex min-w-0 relative overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0 bg-gray-50">

          {/* Chat header */}
          <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between shadow-sm flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setActiveConvId(null)}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-600 flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Back to conversations"
                title="Back">
                <ArrowLeft size={20} />
              </button>
              {activeConv.type === 'dm' ? (
                <Avatar name={getConvName(activeConv)} size={10} online={isOtherOnline(activeConv)} />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <Users size={18} className="text-white" />
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  if (activeConv.type === 'dm') {
                    const other = activeConv.members?.find(m => m.user_id !== myId);
                    if (other) setProfileMember(other);
                  }
                }}
                className="min-w-0 text-left hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate">
                  {getConvName(activeConv)}
                </h3>
                {activeConv.type === 'dm' ? (() => {
                  const other = activeConv.members?.find(m => m.user_id !== myId);
                  const desig = other?.designation;
                  return (
                    <>
                      {desig && <p className="text-[11px] text-gray-500 truncate leading-tight">{desig}</p>}
                      <p className="text-[11px] truncate">
                        {isOtherOnline(activeConv)
                          ? <span className="text-green-600 font-medium">● Online</span>
                          : <span className="text-gray-500">● Offline</span>}
                      </p>
                    </>
                  );
                })() : (
                  <p className="text-[11px] text-gray-500 truncate">
                    {(() => {
                      const onlineCnt = getGroupOnlineCount(activeConv);
                      return onlineCnt > 0
                        ? <span><span className="text-green-600 font-medium">{onlineCnt} online</span> · {activeConv.members?.length || 0} members</span>
                        : <span>{activeConv.members?.length || 0} members</span>;
                    })()}
                  </p>
                )}
              </button>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Create Task button */}
              <button
                onClick={() => setBlankTaskConvId(activeConvId)}
                className="p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:scale-105 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Create Task">
                <ListTodo size={18} />
              </button>
              {/* Search toggle */}
              <button
                onClick={() => { setShowSearch(v => !v); if (showSearch) { setSearchQ(''); setSearchResults([]); } }}
                className={`p-2 rounded-full transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 ${showSearch ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}>
                <Search size={18} />
              </button>
              {activeConv.type !== 'dm' && (
                <button
                  onClick={() => setShowMembersPanel(v => !v)}
                  className={`p-2 rounded-full transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 ${showMembersPanel ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                  title="Group info">
                  <Info size={18} />
                </button>
              )}
              {/* Close chat (deselect) */}
              <button
                onClick={() => {
                  setActiveConvId(null);
                  setThreadParent(null);
                  setThreadReplies([]);
                  setShowMembersPanel(false);
                  setShowChatMenu(false);
                }}
                className="hidden md:flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Close chat">
                <X size={14} /> Close
              </button>
              {activeConv.type === 'dm' && (
                <div className="relative">
                  <button
                    onClick={() => setShowChatMenu(v => !v)}
                    className="p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:scale-105 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="More">
                    <MoreVertical size={18} />
                  </button>
                  {showChatMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowChatMenu(false)} />
                      <div className="absolute right-0 top-10 w-44 bg-white border border-gray-200 rounded-xl shadow-xl py-1 z-20 overflow-hidden">
                        <button
                          onClick={() => { setShowChatMenu(false); handleDeleteDm(); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2.5 text-red-600 transition-colors">
                          <Trash2 size={14} /> Delete Chat
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pinned messages bar */}
          {pins.length > 0 && (
            <PinnedBar
              pins={pins}
              onUnpin={handleUnpin}
              onJump={setHighlightMsgId}
            />
          )}

          {/* In-chat search bar */}
          {showSearch && (
            <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex-shrink-0">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  autoFocus
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors bg-gray-50"
                  placeholder="Search messages…"
                  value={searchQ}
                  onChange={e => handleSearch(e.target.value)} />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-gray-200 flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-600">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
                    <button onClick={() => { setSearchQ(''); setSearchResults([]); }}
                      className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded p-0.5 transition-colors"><X size={13} /></button>
                  </div>
                  {searchResults.map(m => (
                    <button key={m.id} onClick={() => { setSearchQ(''); setSearchResults([]); setShowSearch(false); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-gray-100 border-b border-gray-100 last:border-0 transition-colors">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-gray-900">{m.sender_name}</span>
                        <span className="text-[11px] text-gray-500">{fmtDate(m.created_at)}</span>
                      </div>
                      <p className="text-xs text-gray-600 truncate">{m.content}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages area */}
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto py-4 relative"
          >
            {loadingMsgs && !messages.length ? (
              <div className="space-y-2 px-4">
                {Array.from({ length: 8 }).map((_, i) => <MsgSkeleton key={i} mine={i % 3 === 0} />)}
              </div>
            ) : (
              <>
                {/* Load more */}
                {hasMore && (
                  <div className="flex justify-center mb-3">
                    <button onClick={loadMore} disabled={loadingMsgs}
                      className="flex items-center gap-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm hover:bg-gray-100 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {loadingMsgs
                        ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
                        : <><ChevronDown size={12} /> Load older messages</>}
                    </button>
                  </div>
                )}

                {/* Messages grouped by date + sender (Slack-style). */}
                {groupMessages(messages).map(item => (
                  item.kind === 'date' ? (
                    <DateDivider key={item.key} label={item.label} />
                  ) : (
                    <MessageBubble
                      key={item.msg.id}
                      msg={item.msg}
                      isMine={item.msg.sender_id === myId}
                      seenByOther={seenMsgIds.has(item.msg.id)}
                      highlighted={highlightMsgId === item.msg.id}
                      isPinned={pins.some(p => p.message_id === item.msg.id)}
                      isFirstInGroup={item.isFirstInGroup}
                      isLastInGroup={item.isLastInGroup}
                      onReact={handleReact}
                      onEdit={m => { setEditingMsg(m); setInput(m.content || ''); textareaRef.current?.focus(); }}
                      onDelete={handleDelete}
                      onTask={m => setTaskModal(m)}
                      onTogglePin={handleTogglePin}
                      onThreadOpen={(m) => {
                        // Close members panel if open (thread takes priority)
                        setShowMembersPanel(false);
                        setThreadParent(m);
                      }}
                      onSenderClick={(sid) => {
                        const member = activeConv?.members?.find(m => m.user_id === sid);
                        if (member) setProfileMember(member);
                        else setProfileMember({ user_id: sid, name: item.msg.sender_name } as any);
                      }}
                      senderSubtitle={activeConv?.members?.find(m => m.user_id === item.msg.sender_id)?.designation}
                      currentUserName={user?.name}
                    />
                  )
                ))}

                {/* Typing indicator */}
                <TypingIndicator names={typingNames} />

                <div ref={messagesEndRef} className="h-2" />
              </>
            )}

            {/* Scroll to bottom button */}
            <ScrollToBottomBtn
              visible={showScrollBtn}
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })} />
          </div>

          {/* Edit bar */}
          {editingMsg && (
            <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2 text-xs text-amber-800">
                <div className="p-1 bg-amber-100 rounded"><Edit2 size={11} /></div>
                Editing: <span className="font-medium truncate max-w-xs">{editingMsg.content?.slice(0, 60)}</span>
              </div>
              <button onClick={() => { setEditingMsg(null); setInput(''); }}
                className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500">
                <X size={16} />
              </button>
            </div>
          )}

          {/* Input area */}
          <div className="px-4 py-3 bg-white border-t border-gray-200 flex-shrink-0">
            {/* Formatting toolbar */}
            <div className="flex items-center gap-1 mb-2">
              {([
                { icon: Bold, label: 'Bold', marker: '**' },
                { icon: Italic, label: 'Italic', marker: '*' },
                { icon: Code, label: 'Code', marker: '`' },
              ] as const).map(({ icon: Icon, label, marker }) => (
                <button
                  key={label}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); saveSelection(); wrapSelection(marker); }}
                  className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title={label}>
                  <Icon size={14} />
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              {/* File attachment */}
              <input ref={fileInputRef} type="file" className="hidden"
                onChange={handleFile}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.zip,.txt" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={fileUploading}
                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 hover:scale-105 rounded-full transition-all flex-shrink-0 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Attach file">
                {fileUploading
                  ? <Loader2 size={18} className="animate-spin text-blue-600" />
                  : <Paperclip size={18} />}
              </button>

              {/* Text input */}
              <div className="flex-1 relative">
                {/* Emoji panel — OUTSIDE overflow-hidden, relative to this wrapper */}
                {showInputEmoji && (
                  <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-xl p-2.5 flex flex-wrap gap-1.5 z-30 w-60">
                    {EMOJIS.map(e => (
                      <button
                        key={e}
                        onMouseDown={(ev) => { ev.preventDefault(); insertAtCursor(e); setShowInputEmoji(false); }}
                        className="text-xl hover:scale-110 transition-transform active:scale-95">
                        {e}
                      </button>
                    ))}
                  </div>
                )}
                {/* Mentions autocomplete dropdown */}
                <div className="absolute bottom-full left-0 mb-2 w-80 z-20">
                  <MentionsAutocomplete
                    ref={mentionsRef}
                    visible={mentionQuery !== null}
                    query={mentionQuery ?? ''}
                    users={chatUsers}
                    onSelect={handleMentionSelect}
                  />
                </div>
                <div className="bg-gray-50 rounded-xl border border-gray-200 flex items-end shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent focus-within:bg-white transition-colors">
                  <textarea
                    ref={textareaRef}
                    className="flex-1 px-4 py-2.5 text-[13.5px] leading-relaxed resize-none focus:outline-none bg-transparent min-h-[40px] max-h-[120px]"
                    placeholder={editingMsg ? 'Edit your message…' : 'Type a message…'}
                    rows={1}
                    value={input}
                    onChange={handleTextareaChange}
                    onKeyUp={e => {
                      const t = e.currentTarget;
                      const caret = t.selectionStart ?? t.value.length;
                      setMentionQuery(getMentionQuery(t.value, caret));
                    }}
                    onClick={e => {
                      const t = e.currentTarget;
                      savedSelRef.current = [t.selectionStart, t.selectionEnd];
                      setMentionQuery(getMentionQuery(t.value, t.selectionStart ?? t.value.length));
                    }}
                    onSelect={e => {
                      const t = e.currentTarget as HTMLTextAreaElement;
                      savedSelRef.current = [t.selectionStart, t.selectionEnd];
                    }}
                    onBlur={() => {
                      setTimeout(() => setMentionQuery(null), 120);
                    }}
                    onKeyDown={e => {
                      if (mentionQuery !== null && mentionsRef.current) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault(); e.stopPropagation();
                          mentionsRef.current.moveDown();
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault(); e.stopPropagation();
                          mentionsRef.current.moveUp();
                          return;
                        }
                        if ((e.key === 'Enter' || e.key === 'Tab') && mentionsRef.current.hasSelection()) {
                          e.preventDefault(); e.stopPropagation();
                          mentionsRef.current.pickCurrent();
                          return;
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault(); e.stopPropagation();
                          setMentionQuery(null);
                          return;
                        }
                      }
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                      if (e.key === 'Escape' && editingMsg) {
                        setEditingMsg(null);
                        setInput('');
                      }
                    }} />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setShowInputEmoji(v => !v); }}
                    className="p-2 mr-1 mb-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Emoji">
                    <Smile size={18} />
                  </button>
                </div>
              </div>

              {/* Send button */}
              <button
                onClick={sendMessage}
                disabled={!input.trim() && !editingMsg}
                className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 transition-all flex-shrink-0 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <Send size={16} className="translate-x-0.5" />
              </button>
            </div>
            <p className="text-[11px] text-gray-500 text-center mt-1.5">
              Enter to send · Shift+Enter for new line · Esc to cancel edit
            </p>
          </div>
        </div>

        {/* Thread panel */}
        {threadParent && activeConvId && (
          <ThreadPanel
            conversationId={activeConvId}
            parent={threadParent}
            currentUserId={myId}
            currentUserName={user?.name}
            replies={threadReplies}
            setReplies={setThreadReplies}
            onClose={() => { setThreadParent(null); setThreadReplies([]); }}
            wsSend={wsSend}
          />
        )}

        {/* Task 8: Group members panel */}
        {activeConv.type !== 'dm' && (
          <div className={`${showMembersPanel ? 'flex' : 'hidden md:flex'} fixed inset-0 z-40 md:absolute md:inset-auto md:top-0 md:right-0 md:h-full md:w-72 md:z-20 bg-white md:border-l border-gray-200 flex-col shadow-xl md:transition-transform md:duration-300 ${showMembersPanel ? 'md:translate-x-0' : 'md:translate-x-full'}`}>
            <div className="px-4 py-3.5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">{activeConv.name || 'Group Chat'}</h3>
              <button onClick={() => setShowMembersPanel(false)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500">
                <X size={16} />
              </button>
            </div>

            <div className="px-4 pt-3 pb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest flex-shrink-0">
              Members ({activeConv.members?.length || 0})
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
              {(activeConv.members || []).map(member => {
                const isAdmin = member.role === 'admin';
                const isSelf = member.user_id === myId;
                const callerMember = activeConv.members?.find(m => m.user_id === myId);
                const callerIsAdmin = callerMember?.role === 'admin';
                const canRemove = !isSelf && callerIsAdmin;
                return (
                  <div key={member.user_id} className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors">
                    <button
                      type="button"
                      onClick={() => setProfileMember(member)}
                      className="flex flex-1 min-w-0 items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      <Avatar name={member.name} size={8} online={activeOnlineIds.has(member.user_id)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-gray-900 truncate">{member.name}{isSelf ? ' (you)' : ''}</span>
                          {isAdmin && <Shield size={11} className="text-blue-600 flex-shrink-0" title="Admin" />}
                        </div>
                        {(member.designation || member.department_name) && (
                          <div className="text-[11px] text-gray-500 truncate">
                            {[member.designation, member.department_name].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    </button>
                    {canRemove && (
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Remove member">
                        <UserMinus size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {(() => {
              const callerMember = activeConv.members?.find(m => m.user_id === myId);
              const canDelete = callerMember?.role === 'admin' || (user as any)?.level === 1;
              return canDelete ? (
                <div className="px-3 pb-3 flex-shrink-0">
                  <button
                    onClick={handleDeleteGroup}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-red-500">
                    <Trash2 size={14} /> Delete Group
                  </button>
                </div>
              ) : null;
            })()}
          </div>
        )}
        </div>
      ) : (
        <div className="hidden md:flex flex-1">
          <EmptyState onNew={() => setShowNewConv(true)} />
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      {showNewConv && user && (
        <NewConvModal
          currentUserId={myId}
          onClose={() => setShowNewConv(false)}
          onCreated={conv => {
            setConversations(prev => prev.find(c => c.id === conv.id) ? prev : [conv, ...prev]);
            setActiveConvId(conv.id);
          }} />
      )}

      {taskModal && (
        <TaskCreate
          isOpen
          onClose={() => setTaskModal(null)}
          onCreated={() => setTaskModal(null)}
          chatContext={{
            messageId: taskModal.id,
            conversationId: activeConvId,
            initialTitle: taskModal.content || '',
            initialDescription: taskModal.content || '',
            availableUserIds: activeConv?.members?.map(member => member.user_id),
          }}
        />
      )}

      {blankTaskConvId && (
        <TaskCreate
          isOpen
          onClose={() => setBlankTaskConvId(null)}
          onCreated={() => setBlankTaskConvId(null)}
          chatContext={{
            conversationId: blankTaskConvId,
            availableUserIds: activeConv?.members?.map(member => member.user_id),
          }}
        />
      )}

      {/* Issue 4: Employee profile drawer */}
      {profileMember && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setProfileMember(null)} />
          <div className="fixed right-0 top-0 h-full w-full sm:w-80 bg-white shadow-xl z-50 flex flex-col animate-in slide-in-from-right duration-200 border-l border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-blue-50">
              <h3 className="font-semibold text-gray-900">Profile</h3>
              <button onClick={() => setProfileMember(null)} className="p-1.5 hover:bg-white rounded-lg text-gray-600 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col items-center px-5 py-6 border-b border-gray-200">
                <Avatar name={profileMember.name} size={20} />
                <h4 className="mt-3 text-lg font-semibold text-gray-900 text-center">{profileMember.name || 'Unknown'}</h4>
                {profileMember.designation && (
                  <p className="text-sm text-gray-500 mt-0.5">{profileMember.designation}</p>
                )}
                {profileMember.role && (
                  <span className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
                    {profileMember.role}
                  </span>
                )}
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                {profileMember.email && (
                  <div className="flex items-start gap-3">
                    <Mail size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide">Email</div>
                      <div className="text-gray-900 truncate">{profileMember.email}</div>
                    </div>
                  </div>
                )}
                {profileMember.phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide">Phone</div>
                      <div className="text-gray-900">{profileMember.phone}</div>
                    </div>
                  </div>
                )}
                {profileMember.department_name && (
                  <div className="flex items-start gap-3">
                    <Building2 size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide">Department</div>
                      <div className="text-gray-900">{profileMember.department_name}</div>
                    </div>
                  </div>
                )}
                {profileMember.designation && (
                  <div className="flex items-start gap-3">
                    <Briefcase size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide">Designation</div>
                      <div className="text-gray-900">{profileMember.designation}</div>
                    </div>
                  </div>
                )}
                {!profileMember.email && !profileMember.phone && !profileMember.department_name && !profileMember.designation && (
                  <p className="text-xs text-gray-500 text-center py-4">No additional details available.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
